import React, { createContext, useContext, useRef, useState, useCallback } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '../ui/alert-dialog';
import { cn } from '../../lib/utils';

export interface ConfirmOptions {
  title?: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Красная кнопка подтверждения для опасных действий (удаление и т.п.). */
  destructive?: boolean;
}

type ConfirmFn = (options?: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

/**
 * Промис-API подтверждения вместо нативного confirm().
 * Использование: `const confirm = useConfirm(); if (!(await confirm({...}))) return;`
 */
export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm должен использоваться внутри ConfirmProvider');
  return ctx;
}

export function ConfirmProvider({ isDark, children }: { isDark: boolean; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmOptions>({});
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((opts = {}) => {
    setOptions(opts);
    setOpen(true);
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const resolve = useCallback((value: boolean) => {
    setOpen(false);
    resolverRef.current?.(value);
    resolverRef.current = null;
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={open} onOpenChange={(o) => { if (!o) resolve(false); }}>
        <AlertDialogContent className={cn(isDark ? 'bg-[#1A1A22] border-slate-700' : 'bg-white')}>
          <AlertDialogHeader>
            <AlertDialogTitle className={cn(isDark ? 'text-white' : 'text-[#111111]')}>
              {options.title ?? 'Подтвердите действие'}
            </AlertDialogTitle>
            {options.description && (
              <AlertDialogDescription className={cn(isDark ? 'text-slate-400' : 'text-slate-500')}>
                {options.description}
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => resolve(false)}
              className={cn(isDark ? 'bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white' : '')}
            >
              {options.cancelLabel ?? 'Отмена'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => resolve(true)}
              className={cn(
                'text-white',
                options.destructive ? 'bg-red-600 hover:bg-red-600/90' : 'bg-[#C9A227] hover:bg-[#C9A227]/90'
              )}
            >
              {options.confirmLabel ?? 'Подтвердить'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}
